import Keycloak from 'keycloak-js';
import { setTokenRefresher } from './client';

/**
 * OIDC/Keycloak-Adapter (1:1 nach SMS-PRO-Muster, keycloak-js ^26).
 *
 * Prinzip: keycloak-js hält die Session; das Access-Token wird in
 * `localStorage['pm_token']` GESPIEGELT. Der apiClient liest Token + Tenant aus
 * localStorage und hängt Bearer + X-Tenant-Id an jeden Request — die Lib bleibt
 * so vom apiClient entkoppelt (kein Interceptor).
 *
 * Config kommt aus Build-Time-Env (VITE_KEYCLOAK_*). Voraussetzung: statische
 * Datei `public/silent-check-sso.html`.
 */

let kc: Keycloak | null = null;

function instance(): Keycloak {
  if (kc) return kc;
  kc = new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  });
  return kc;
}

let initPromise: Promise<boolean> | null = null;

/** Initialisiert keycloak-js (check-sso, still). Idempotent. Spiegelt bei
 *  bestehender Session das Token nach localStorage und registriert den
 *  Refresher beim apiClient. */
export function initKeycloak(): Promise<boolean> {
  if (initPromise) return initPromise;
  initPromise = instance()
    .init({
      onLoad: 'check-sso',
      // KEIN silentCheckSsoRedirectUri (bewusst entfernt): der stille Session-Check
      // von keycloak-js laeuft sonst ueber einen versteckten iframe, der die
      // Keycloak-Auth-URL einbettet. PRODs Keycloak (rzob.id-manager.de) verbietet
      // das Einbetten per CSP 'frame-ancestors self' -> der iframe wird geblockt und
      // der Boot haengt bei "Anmeldung wird geprueft...". Ohne diese Option macht
      // check-sso den Check per (prompt=none-)Redirect statt iframe -> kein Framing,
      // kein CSP-Block. (Staging-Keycloak erlaubt das Framing, prod nicht.)
      pkceMethod: 'S256',
      checkLoginIframe: false,
      // email + profile explizit anfordern (openid ergaenzt keycloak-js selbst).
      scope: 'email profile',
    })
    .then((authenticated) => {
      const token = instance().token;
      if (authenticated && token) {
        try { localStorage.setItem('pm_token', token); } catch { /* ignore */ }
        scheduleRefresh();
      }
      // Refresher beim apiClient registrieren: bei 401 forciert Token erneuern + spiegeln.
      setTokenRefresher(async () => {
        try {
          await instance().updateToken(-1); // -1 = forced refresh
          const fresh = instance().token;
          if (fresh) { try { localStorage.setItem('pm_token', fresh); } catch { /* ignore */ } }
          return fresh ?? null;
        } catch {
          return null;
        }
      });
      return authenticated;
    })
    .catch(() => false);
  return initPromise;
}

/** Startet den Login-Redirect (Authorization Code + PKCE). */
export function keycloakLogin(redirectUri?: string): void {
  void instance().login({ redirectUri: redirectUri ?? window.location.origin });
}

/** Logout am Keycloak + Redirect zurück auf die App. */
export function keycloakLogout(): void {
  void instance().logout({ redirectUri: window.location.origin });
}

/** Token periodisch (30s) erneuern, wenn <60s Restgültigkeit — und spiegeln. */
function scheduleRefresh(): void {
  setInterval(() => {
    void instance()
      .updateToken(60)
      .then((refreshed) => {
        const token = instance().token;
        if (refreshed && token) {
          try { localStorage.setItem('pm_token', token); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* Refresh fehlgeschlagen → regulärer 401 → Session-Expired-Flow */ });
  }, 30_000);
}
